import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  DatePicker,
  message,
} from "antd";
import { usePayments } from "../../../hooks/finance/usePayments";
import { useNavigate } from "react-router-dom";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";
import dayjs from "dayjs";

const { TextArea } = Input;
const { Option } = Select;

const CreateManualPaymentModal = ({ visible, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { createManualPayment } = usePayments();
  const [submitting, setSubmitting] = useState(false);
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      form.setFieldsValue({
        paymentDate: dayjs(),
        paymentMethod: "Bank Transfer",
      });
      setSelectedStudent(null);
      fetchStudents();
    }
  }, [visible, form]);

  const fetchStudents = async () => {
    setLoadingStudents(true);
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        `${domainSettings.urls.enrollment.student}?page_size=1000`,
        null,
        navigate,
      );

      if (response.ok) {
        const data = response.data.results || response.data || [];
        setStudents(data);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleStudentSelect = (spcCode) => {
    const student = students.find((s) => s.spcCode === spcCode);
    setSelectedStudent(student);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const paymentData = {
        spc: values.studentSpc,
        amount: values.amount,
        paymentDate: values.paymentDate.toISOString(),
        paymentMethod: values.paymentMethod,
        paymentChannel: "Manual",
        notes: values.notes || "",
      };

      const result = await createManualPayment(paymentData);

      if (result) {
        message.success(
          "Manual payment created successfully. Please validate to allocate.",
        );
        form.resetFields();
        setSelectedStudent(null);
        onSuccess();
      }
    } catch (error) {
      console.error("Create payment error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Create Manual Payment"
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      okText="Create Payment"
      confirmLoading={submitting}
      width={600}
      okButtonProps={{ disabled: submitting }}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="studentSpc"
          label="Student"
          rules={[{ required: true, message: "Please select a student" }]}
        >
          <Select
            showSearch
            optionFilterProp="children"
            onChange={handleStudentSelect}
            placeholder="Select a student"
            loading={loadingStudents}
            filterOption={(input, option) => {
              const text = Array.isArray(option.children)
                ? option.children.join("")
                : String(option.children || "");
              return text.toLowerCase().includes(input.toLowerCase());
            }}
          >
            {students.map((student) => (
              <Option key={student.spcCode} value={student.spcCode}>
                {student.firstName} {student.lastName} -{" "}
                {student.admissionID || student.spcCode} (
                {student.className || "No Class"})
              </Option>
            ))}
          </Select>
        </Form.Item>

        {selectedStudent && (
          <div
            style={{
              background: "#f6ffed",
              border: "1px solid #b7eb8f",
              borderRadius: "4px",
              padding: "12px",
              marginBottom: "16px",
            }}
          >
            <strong>Selected Student:</strong>
            <p style={{ marginBottom: 0, marginTop: 4 }}>
              {selectedStudent.firstName} {selectedStudent.lastName}
              <br />
              <small>
                Admission #: {selectedStudent.admissionID || "N/A"} | SPC:{" "}
                {selectedStudent.spcCode} | Class:{" "}
                {selectedStudent.className || "N/A"}
              </small>
            </p>
          </div>
        )}

        <Form.Item
          name="amount"
          label="Amount"
          rules={[
            { required: true, message: "Please enter the payment amount" },
            {
              validator: (_, value) => {
                if (value && value <= 0) {
                  return Promise.reject("Amount must be greater than 0");
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            placeholder="Enter payment amount"
            min={0.01}
            step={100}
            precision={2}
            formatter={(value) =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
            parser={(value) => value.replace(/,/g, "")}
          />
        </Form.Item>

        <Form.Item
          name="paymentDate"
          label="Payment Date"
          rules={[
            { required: true, message: "Please select the payment date" },
          ]}
        >
          <DatePicker
            style={{ width: "100%" }}
            showTime
            format="YYYY-MM-DD HH:mm"
            placeholder="Select payment date and time"
          />
        </Form.Item>

        <Form.Item
          name="paymentMethod"
          label="Payment Method"
          rules={[
            { required: true, message: "Please select the payment method" },
          ]}
        >
          <Select placeholder="Select payment method">
            <Option value="Bank Transfer">Bank Transfer</Option>
            <Option value="Mobile Money">Mobile Money</Option>
            <Option value="Card">Card</Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Payment Channel"
          extra="Payer, bank, slip, or office details can be entered in Notes"
        >
          <Input value="Manual" disabled />
        </Form.Item>

        <Form.Item name="notes" label="Notes (Optional)">
          <TextArea
            rows={3}
            placeholder="Any additional notes about this payment (e.g., depositor name, bank slip number)"
          />
        </Form.Item>
      </Form>

      <div
        style={{
          background: "#fff7e6",
          border: "1px solid #ffd591",
          borderRadius: "4px",
          padding: "12px",
          marginTop: "16px",
        }}
      >
        <strong>Next Steps:</strong>
        <ol style={{ marginBottom: 0, marginTop: 8, paddingLeft: "20px" }}>
          <li>
            After creating, the payment will appear with "Pending Validation"
            status
          </li>
          <li>
            Click "Validate" on the payment to verify and allocate funds to fees
          </li>
          <li>
            Validated payments will automatically be applied to outstanding
            balances
          </li>
        </ol>
      </div>
    </Modal>
  );
};

export default CreateManualPaymentModal;
